import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api, type SyncRun } from "../lib/api";
import { Graph } from "../components/topology/Graph";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { Code } from "../components/primitives/Code";
import { Table, type Column } from "../components/primitives/Table";
import { PageHeader } from "../components/primitives/PageHeader";
import { TechnicalDetails } from "../components/primitives/TechnicalDetails";
import { useConfirm } from "../components/primitives/Confirm";
import { PageHelp } from "../components/PageHelp";
import { modeLabel } from "../lib/mode-labels";
import { runTone, runReason } from "../lib/run-status";
import { runStatusLabel } from "../lib/display-labels";
import { transformDescription, vendorLabel } from "../lib/friendly-labels";
import { ts } from "./Activity";

/**
 * One pipeline (spec §6.5): what it moves, how it has been running, and its
 * recent runs.
 *
 * Pause is reversible, so it is Tier 0. Turn on resumes writes to a live
 * destination, which makes it Tier 1. Raw config stays in Technical details.
 */
export function Pipeline() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const confirm = useConfirm();
  const q = usePolling(() => api.getPipeline(id!), 5000);
  const runs = usePolling(() => api.listSyncLog({ pipelineId: id, limit: 20 }), 5000);
  const connectorsQ = usePolling(() => api.listConnectors(), 60000);
  const transformsQ = usePolling(() => api.listTransforms(), 60000);
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  if (q.status === "loading" || !q.data) return <Pulse />;

  const p = q.data.pipeline;
  const connectors = connectorsQ.data?.connectors ?? [];
  const transforms = transformsQ.data?.transforms ?? [];
  // The API returns `enabled: boolean`; tolerate the older `disabled` shape.
  const enabled: boolean = p.enabled === true || (p.enabled == null && p.disabled !== true);

  const sourceVendor = vendorLabel(p.sourceInstance?.connectorType ?? p.sourceConnectorType, connectors);
  const destVendor = vendorLabel(p.destInstance?.connectorType ?? p.destConnectorType, connectors);
  const sourceLabel = p.sourceInstance ? `${sourceVendor} / ${p.sourceInstance.name}` : sourceVendor;
  const destLabel = p.destInstance ? `${destVendor} / ${p.destInstance.name}` : destVendor;
  const triggerMode: string | undefined = p.triggers?.[0]?.mode;

  /** Stop syncing. No confirmation: pausing is reversible and stops nothing already
   *  written, and the poll reflects the new state within a few seconds. */
  async function pause() {
    if (toggling) return;
    setToggleError(null);
    setToggling(true);
    try {
      await api.disablePipeline(id!);
      // usePolling picks the new state up within 5s.
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : String(err));
    } finally {
      setToggling(false);
    }
  }

  /** Start syncing again, after confirming — this one does begin writing to the
   *  destination, so it is the direction worth a question. */
  async function turnOn() {
    if (toggling) return;
    setToggleError(null);
    await confirm({
      tier: 1,
      title: `Turn on ${p.name}?`,
      body: `Events for this pipeline will start syncing to ${destVendor} again.`,
      confirmLabel: "Turn on pipeline",
      onConfirm: async () => { await api.enablePipeline(id!); },
    });
  }

  const nodes = [
    { id: "source", label: sourceLabel, kind: "source" as const, state: "idle" as const },
    { id: "transform", label: transformDescription(p.transformId, transforms), kind: "transform" as const, state: "idle" as const },
    { id: "dest", label: destLabel, kind: "destination" as const, state: "idle" as const },
  ];
  const edges = [
    { source: "source", target: "transform" },
    { source: "transform", target: "dest" },
  ];

  const recentRunColumns: Column<SyncRun>[] = [
    { key: "record", label: "RECORD", mono: true, render: (r) => <span title={r.eventId}>{r.objectType} · {r.operation}</span> },
    { key: "status", label: "STATUS", render: (r) => <Badge tone={runTone(r.status)}>{runStatusLabel(r.status)}</Badge> },
    { key: "when", label: "WHEN", mono: true, render: (r) => ts(r.receivedAt) },
    {
      key: "reason",
      label: "REASON",
      render: (r) => {
        const reason = runReason(r);
        return reason ? <span className="run-reason" title={reason}>{reason}</span> : <span className="run-reason run-reason--empty">—</span>;
      },
    },
  ];

  const configJson = JSON.stringify(p, null, 2);

  return (
    <div>
      <PageHeader
        eyebrow="Pipeline"
        title={p.name}
        meta={p.id}
        summary={`${transformDescription(p.transformId, transforms)} · ${sourceVendor} → ${destVendor}`}
        actions={
          <>
            {triggerMode && <Badge tone="muted">{modeLabel(triggerMode)}</Badge>}
            <Badge tone={enabled ? "ok" : "muted"}>{enabled ? "Active" : "Paused"}</Badge>
            <Button variant="secondary" onClick={() => nav(`/pipelines/${id}/edit`)}>Edit</Button>
            {enabled ? (
              <Button variant="secondary" onClick={() => void pause()} disabled={toggling}>
                {toggling ? "Working…" : "Pause"}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => void turnOn()} disabled={toggling}>
                Turn on
              </Button>
            )}
          </>
        }
      />
      {toggleError && <div className="field__error" style={{ marginBottom: 12 }}>{toggleError}</div>}
      <PageHelp />

      <Graph nodes={nodes} edges={edges} width={720} height={280} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 32 }}>
        <div className="uppercase-label">RECENT RUNS</div>
        <Button variant="ghost" onClick={() => nav(`/activity?pipelineId=${encodeURIComponent(id!)}`)}>
          All runs
        </Button>
      </div>
      <div style={{ marginTop: 8 }}>
        <Table
          columns={recentRunColumns}
          rows={runs.data?.entries ?? []}
          rowKey={(r) => r.id}
          onRowClick={(r) => nav(`/activity/${r.id}`)}
          empty={<p className="page-help">No records crossed this path yet.</p>}
        />
      </div>

      <div style={{ marginTop: 32 }}>
        <TechnicalDetails title="Pipeline configuration" copyText={configJson}>
          <Code block>{configJson}</Code>
        </TechnicalDetails>
      </div>
    </div>
  );
}
